import React, { useState, useEffect, useCallback } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Line, Pie } from 'react-chartjs-2'
import { getNotificationStats } from '../../api/adminNotificationsService'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
)

/**
 * NotificationStats - Display notification statistics with charts
 */
function NotificationStats() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dateRange, setDateRange] = useState('30') // Default to last 30 days

  const loadStats = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Calculate date range
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - parseInt(dateRange))

      const response = await getNotificationStats(
        start.toISOString(),
        end.toISOString(),
      )

      if (response.success) {
        setStats(response.stats)
      } else {
        throw new Error(response.error || 'Failed to load stats')
      }
    } catch (err) {
      console.error('Error loading stats:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [dateRange])

  useEffect(() => {
    loadStats()
  }, [loadStats])

  // Prepare chart data
  const getDailySendsChartData = () => {
    if (!stats || !stats.daily_sends) return null

    const dates = Object.keys(stats.daily_sends).sort()
    const smsData = dates.map((date) => stats.daily_sends[date].sms)
    const emailData = dates.map((date) => stats.daily_sends[date].email)

    return {
      labels: dates.map((date) => new Date(date).toLocaleDateString()),
      datasets: [
        {
          label: 'SMS',
          data: smsData,
          borderColor: 'rgb(212, 233, 76)',
          backgroundColor: 'rgba(212, 233, 76, 0.5)',
          tension: 0.3,
        },
        {
          label: 'Email',
          data: emailData,
          borderColor: 'rgb(37, 99, 235)',
          backgroundColor: 'rgba(37, 99, 235, 0.5)',
          tension: 0.3,
        },
      ],
    }
  }

  const getEventTypeChartData = () => {
    if (!stats || !stats.by_event_type) return null

    const eventTypes = Object.keys(stats.by_event_type)
    const counts = Object.values(stats.by_event_type)

    return {
      labels: eventTypes.map((type) =>
        type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
      ),
      datasets: [
        {
          data: counts,
          backgroundColor: [
            'rgba(212, 233, 76, 0.8)',
            'rgba(37, 99, 235, 0.8)',
            'rgba(29, 78, 216, 0.8)',
            'rgba(166, 194, 53, 0.8)',
            'rgba(96, 165, 250, 0.8)',
          ],
          borderWidth: 1,
        },
      ],
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading stats...</span>
        </div>
        <p className="mt-2 text-muted">Loading notification statistics...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h5 className="alert-heading">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Error Loading Stats
        </h5>
        <p className="mb-0">{error}</p>
        <button className="btn btn-sm btn-danger mt-2" onClick={loadStats}>
          <i className="bi bi-arrow-clockwise me-1"></i>
          Retry
        </button>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2"></i>
        No statistics available
      </div>
    )
  }

  const dailySendsData = getDailySendsChartData()
  const eventTypeData = getEventTypeChartData()

  return (
    <div className="notification-stats">
      {/* Date Range Selector */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h5 className="mb-0">Statistics Overview</h5>
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${dateRange === '7' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setDateRange('7')}
              >
                Last 7 Days
              </button>
              <button
                type="button"
                className={`btn btn-sm ${dateRange === '30' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setDateRange('30')}
              >
                Last 30 Days
              </button>
              <button
                type="button"
                className={`btn btn-sm ${dateRange === '90' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setDateRange('90')}
              >
                Last 90 Days
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-6 col-lg-3">
          <div className="card stats-card shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <p className="text-muted mb-1 small">Total Segments</p>
                  <h3 className="mb-0">{stats.total_segments || 0}</h3>
                </div>
                <i className="bi bi-envelope-fill text-primary fs-2"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-3">
          <div className="card stats-card shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <p className="text-muted mb-1 small">Estimated Cost</p>
                  <h3 className="mb-0 cost-badge">
                    ${(stats.estimated_cost || 0).toFixed(2)}
                  </h3>
                </div>
                <i className="bi bi-currency-dollar text-success fs-2"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-3">
          <div className="card stats-card shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <p className="text-muted mb-1 small">Event Types</p>
                  <h3 className="mb-0">
                    {Object.keys(stats.by_event_type || {}).length}
                  </h3>
                </div>
                <i className="bi bi-tag-fill text-info fs-2"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-3">
          <div className="card stats-card shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <p className="text-muted mb-1 small">Recent Failures</p>
                  <h3 className="mb-0 text-danger">
                    {stats.recent_failures?.length || 0}
                  </h3>
                </div>
                <i className="bi bi-exclamation-triangle-fill text-danger fs-2"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="row g-3 mb-4">
        {/* Daily Sends Chart */}
        <div className="col-12 col-lg-8">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title mb-3">
                <i className="bi bi-graph-up me-2"></i>
                Daily Sends
              </h5>
              {dailySendsData ? (
                <Line
                  data={dailySendsData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    aspectRatio: 2,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          precision: 0,
                        },
                      },
                    },
                  }}
                />
              ) : (
                <p className="text-muted">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Event Type Breakdown */}
        <div className="col-12 col-lg-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title mb-3">
                <i className="bi bi-pie-chart me-2"></i>
                By Event Type
              </h5>
              {eventTypeData ? (
                <Pie
                  data={eventTypeData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          boxWidth: 12,
                          font: {
                            size: 11,
                          },
                        },
                      },
                    },
                  }}
                />
              ) : (
                <p className="text-muted">No data available</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Top Users Table */}
      {stats.top_users && stats.top_users.length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-body">
                <h5 className="card-title mb-3">
                  <i className="bi bi-people-fill me-2"></i>
                  Top Users by Send Count
                </h5>
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th className="text-end">Send Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top_users.map((user, index) => (
                        <tr key={user.user_id || index}>
                          <td>{user.name}</td>
                          <td className="text-muted small">{user.email}</td>
                          <td className="text-end">
                            <span className="badge bg-primary">
                              {user.send_count}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Failures Table */}
      {stats.recent_failures && stats.recent_failures.length > 0 && (
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-body">
                <h5 className="card-title mb-3">
                  <i className="bi bi-exclamation-triangle-fill text-danger me-2"></i>
                  Recent Failures
                </h5>
                <div className="table-responsive">
                  <table className="table table-hover failure-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>User</th>
                        <th>Event Type</th>
                        <th>Channel</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recent_failures.map((failure, index) => (
                        <tr key={failure.id || index}>
                          <td className="text-muted small">
                            {new Date(failure.created_at).toLocaleString()}
                          </td>
                          <td>{failure.user_name || 'Unknown'}</td>
                          <td>
                            <span className="badge bg-secondary">
                              {failure.event_type}
                            </span>
                          </td>
                          <td>{failure.channel}</td>
                          <td className="text-danger small">
                            {failure.failure_reason || 'Unknown'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Failure Breakdown */}
                {stats.failure_breakdown &&
                  Object.keys(stats.failure_breakdown).length > 0 && (
                    <div className="mt-3">
                      <h6 className="text-muted">Failure Breakdown:</h6>
                      <div className="d-flex flex-wrap gap-2">
                        {Object.entries(stats.failure_breakdown).map(
                          ([reason, count]) => (
                            <span
                              key={reason}
                              className="badge bg-danger"
                              title={`${count} failures`}
                            >
                              {reason}: {count}
                            </span>
                          ),
                        )}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {Object.keys(stats.daily_sends || {}).length === 0 && (
        <div className="alert alert-info text-center">
          <i className="bi bi-info-circle fs-2 d-block mb-2"></i>
          <h5>No Notifications Sent Yet</h5>
          <p className="mb-0 text-muted">
            Once notifications start being sent, statistics will appear here.
          </p>
        </div>
      )}
    </div>
  )
}

export default NotificationStats
